// Store model types
import type { Brand } from './Brand'
import type { Partner } from './Partner'
import type { Category } from './Category'
import type { Contact } from './Contact'
import type { DietaryOption } from './DietaryOption'
import type { Event } from './Event'
import type { User } from './User'
import type { StoreReview } from './StoreReview'
import {
  StoreStatusSchema,
  type MenuListItemDto,
  type StoreDto,
  type StoreOperatingHourEntry,
  type StoreStatus as GeneratedStoreStatus,
  type StoreStoreRequest,
  type StoreUpdateDetailsRequest,
  type StoreUpdateHoursRequest,
  type StoreUpdateLeadTimeRequest,
  type StoreUpdateServicesRequest,
  type StoreUpdateVenueRequest
} from '~/services/api/generated'

export type StoreStatus = GeneratedStoreStatus

type FacadeKey<Value extends string> = Value extends `${infer Prefix}_${infer Suffix}`
  ? `${Capitalize<Prefix>}${FacadeKey<Suffix>}`
  : Capitalize<Value>

type StoreStatusFacade = {
  [Value in StoreStatus as FacadeKey<Value>]: Value
}

const statusValue = <Value extends StoreStatus>(value: Value): Value => {
  StoreStatusSchema.parse(value)
  return value
}

export const StoreStatus = {
  Draft: statusValue('draft'),
  InReview: statusValue('in_review'),
  UpdatesRequired: statusValue('updates_required'),
  Active: statusValue('active'),
  TemporarilyClosed: statusValue('temporarily_closed'),
  Disabled: statusValue('disabled')
} satisfies StoreStatusFacade
export const StoreStatusValues: readonly StoreStatus[] = StoreStatusSchema.options
export type StoreOrderType = 'delivery' | 'pickup' | 'dine_in'
export type StoreLeadTimeType = 'same_day' | 'day_before' | 'multiple_days'
export type StoreImageType = 'cover' | 'logo'
export type StoreDayOfWeek =
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'

export interface StoreTemporaryClosure {
  id?: number | string
  starts_at?: string | null
  startsAt?: string | null
  ends_at?: string | null
  endsAt?: string | null
}

export type StoreReviewFeedback = NonNullable<StoreDto['review_feedback']>

export type StoreReviewFieldUpdateRequest = StoreReviewFeedback['field_update_requests'][number]

export interface StoreHealth {
  score: number
  total: number
  percentage: number
  issues: string[]
  completed: string[]
}

export interface StoreScoreFactor {
  value: number | null
  score: number | null
  weight: number
  included: boolean
  // acceptance factor carries raw counts
  accepted?: number
  declined?: number
  auto_declined?: number
}

/**
 * Operational health for an active store (GET /stores/{id}/health).
 * Setup completeness for non-active stores lives in setup-progress instead.
 * score/breakdown are null until the store has been scored (nightly command).
 */
export interface StoreOperationalHealth {
  average_rating: number | null
  review_count: number
  is_reliability_superstar: boolean
  score: number | null
  score_updated_at: string | null
  breakdown: Record<string, unknown> | null
}

export interface Store {
  id: number
  brand_id?: number
  external_source?: string
  external_id?: string
  external_data?: Record<string, unknown>
  partner_id: number
  name: string
  slug: string
  description?: string | null
  cover?: string | null
  logo?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  latitude?: number
  longitude?: number
  /** IANA timezone identifier, e.g. "America/New_York" */
  timezone?: string
  status: StoreStatus
  onboarding_review_status?:
    'pending' | 'resubmitted' | 'updates_required' | 'approved' | string | null
  review_requested_at?: string | null
  reviewed_at?: string | null
  review_feedback?: StoreReviewFeedback | null
  is_temporarily_closed?: boolean
  isTemporarilyClosed?: boolean
  temporary_closures?: StoreTemporaryClosure[]
  temporaryClosures?: StoreTemporaryClosure[]
  active_temporary_closure?: StoreTemporaryClosure | null
  activeTemporaryClosure?: StoreTemporaryClosure | null
  delivery_radius: number
  minimum_order: number
  delivery_fee: number | null
  delivery_available: boolean
  pickup_available: boolean
  dine_in_available: boolean
  /** Enabled fulfillment channels (e.g. from dashboard store list). */
  order_types?: StoreOrderType[] | string[]
  pickup_instructions?: string
  lead_time_type: string
  lead_time_hours: number
  day_before_cutoff_time?: string
  multiple_days_notice?: number
  created_at?: string // ISO date string
  updated_at?: string // ISO date string
  average_rating: number | null
  review_count: number
  is_featured: boolean
  featured_until?: string // ISO date string
  featured_sort_order?: number
  is_reliability_superstar: boolean
  supports_group_orders: boolean
  individual_packaging_available: boolean
  enable_google_reviews?: boolean
  google_places_address?: string
  formatted_address?: string
  lead_time_description: string
  is_favorite: boolean
  brand?: Brand
  partner?: Partner
  categories?: Category[]
  categories_count?: number
  contacts?: Contact[]
  contacts_count?: number
  dietary_options?: DietaryOption[]
  dietary_options_count?: number
  events?: Event[]
  events_count?: number
  favorited_by_users?: User[]
  favorited_by_users_count?: number
  managers?: User[]
  managers_count?: number
  menus?: MenuListItemDto[]
  menus_count?: number
  operating_hours?: StoreOperatingHourEntry[]
  operating_hours_count?: number
  orders_count?: number
  reviews?: StoreReview[]
  reviews_count?: number
  approved_reviews?: StoreReview[]
  approved_reviews_count?: number
  tag_ids?: number[]
}

// Request types
export type CreateStoreRequest = StoreStoreRequest
export type UpdateStoreDetailsRequest = NonNullable<StoreUpdateDetailsRequest>
export type UpdateStoreLeadTimeRequest = NonNullable<StoreUpdateLeadTimeRequest>
export type UpdateStoreOperatingHoursRequest = StoreUpdateHoursRequest
export type UpdateStoreServicesRequest = NonNullable<StoreUpdateServicesRequest>
export type UpdateStoreVenueRequest = StoreUpdateVenueRequest

export interface UpdateStoreMenusOrderRequest {
  menus: {
    menu_id: number | string
    order: number
  }[]
}

export interface UpdateStoreStatusRequest {
  status: StoreStatus
}

export interface UpdateStoreImageRequest {
  type: StoreImageType
  image: File
}
